import PDFMake from 'pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import i18n from '@/i18n'
import { ContractMode, PayBy, PayOn } from '@/utils/Enumerations'
import { formatMoney } from '@/utils/NumericMutations'
import { fileNameSafeString } from '@/utils/Files'
import { generateSvgForPdf, alphaRCircleOutline } from '@/images/AngelSvgLibrary'
import { getFooter, getMargin } from '@/utils/pdf-formatters/pdfHelpers'
import { fetchRateSheetById, rateSheetTableBlock } from '@/utils/pdf-formatters/RateSheet'
import {
  contentTypeCode,
  distanceSuffix,
  documentStyles,
  divider,
  embeddedRateSheetName,
  getRateSheets,
  gridRowFillLayout,
  headerBlock,
  infoBlock,
  infoSection,
  joinWithConjunction,
  sortByProductName
} from '@/utils/pdf-formatters/GenericContractFunctions'

PDFMake.vfs = pdfFonts.pdfMake.vfs

const PAGE_MARGIN = 40
// No pageSize is set, so pdfmake renders A4; 841.89pt is its landscape width.
const CONTENT_WIDTH = 841.89 - (PAGE_MARGIN * 2)
const GRID_CELL_PADDING = 8
// pdfmake clips the running header to the top margin, so it has to be deep enough to hold it.
const TOP_MARGIN = 56
const RUNNING_HEADER_MARGIN = 16
const FOOTER_TEXT_TOP = 10
const RATE_SHEET_BLOCK = 1
const CHIP_BORDER_COLOR = '#AAAAAA'
const CHIP_GAP = 6
const CHIP_CHROME = 13
// pdfmake exposes no text metrics while building a definition, so box widths are estimated from
// average 8pt Roboto advances, rounded up so a misjudged row loses a box rather than wrapping one.
const BOLD_CHAR = 4.2
const CHAR = 3.9

export async function generateConsolidatedContractPdf ({ activityRows, ...params }) {
  const pdf = await getConsolidatedContractPdf(params, activityRows)
  pdf.download(`${consolidatedFileName(params)}.pdf`)
}

function consolidatedFileName ({ contract, tract, contractMode }) {
  const origin = contractMode.value === ContractMode.Logs.value ? tract?.name : contract.fromAccount
  return [
    contentTypeCode(contractMode),
    'Contract',
    fileNameSafeString(origin || ''),
    fileNameSafeString(contract.account || ''),
    fileNameSafeString(contract.destination || '')
  ].join('-')
}

function contractOrigin ({ contract, tract }) {
  return tract?.name || contract.fromAccount || contract.account || ''
}

async function getConsolidatedContractPdf (params, activityRows) {
  const rows = sortRows(activityRows)
  const legendColumns = runtimeLegendColumns(rows)
  const rateSheetGroups = await fetchRateSheetsByCounterparty(rows)
  const sheetNames = rateSheetNamesById(rateSheetGroups)

  const content = [
    headerBlock(params, { title: i18n.t('consolidatedContractDetail') }),
    infoBlock(params),
    {
      margin: getMargin({ top: 25 }),
      stack: infoSection(i18n.t('activities'), activityGrid(params, rows, deductionNotes(rows), sheetNames))
    },
    ...rateSheetSection(rateSheetGroups)
  ]

  return PDFMake.createPdf({
    content,
    pageOrientation: 'landscape',
    pageMargins: [PAGE_MARGIN, TOP_MARGIN, PAGE_MARGIN, PAGE_MARGIN],
    styles: documentStyles,
    header: currentPage => currentPage > 1 ? runningHeader(params) : '',
    footer: (currentPage, pageCount) => documentFooter(currentPage, pageCount, legendColumns),
    pageBreakBefore: moveStraddlingSheetToNextPage
  })
}

// A rate sheet that would be split only because something precedes it on the page starts the next
// page whole instead. One genuinely taller than a page is already alone at the top, so it breaks
// rather than recursing. pdfmake drops an `unbreakable` block that tall, which is why this is a
// page-break callback and not that flag.
function moveStraddlingSheetToNextPage (node, followingNodesOnPage, nodesOnNextPage, previousNodesOnPage) {
  return node.headlineLevel === RATE_SHEET_BLOCK &&
    node.pageNumbers.length > 1 &&
    previousNodesOnPage.some(previous => !previous.stack)
}

function runningHeader (params) {
  const origin = contractOrigin(params)
  return {
    margin: getMargin({ left: PAGE_MARGIN, top: RUNNING_HEADER_MARGIN, right: PAGE_MARGIN }),
    stack: [
      { text: params.companyInfo.name, style: 'header' },
      ...(origin ? [{ text: origin, style: 'subtitle' }] : []),
      divider({ margin: getMargin({ top: 6 }) })
    ]
  }
}

function documentFooter (currentPage, pageCount, legendColumns) {
  return {
    ...getFooter(currentPage,
      false,
      getMargin({ left: 30, top: 0, right: 30, bottom: 10 }),
      FOOTER_TEXT_TOP,
      pageCount,
      legendColumns),
    style: 'subtitle'
  }
}

function runtimeLegendColumns (rows) {
  if (!rows.some(row => row.isRuntime)) return []
  return [
    { svg: generateSvgForPdf(alphaRCircleOutline), width: 9, margin: getMargin({ top: FOOTER_TEXT_TOP }) },
    { text: i18n.t('runtimeCounterparty'), width: 'auto', margin: getMargin({ left: 2, top: FOOTER_TEXT_TOP, right: 8 }) }
  ]
}

const sortRows = rows => [...rows].sort((a, b) =>
  (a.accountId ? 0 : 1) - (b.accountId ? 0 : 1) ||
  (a.name ?? '').localeCompare(b.name ?? '') ||
  a.activity.activityTemplate.name.localeCompare(b.activity.activityTemplate.name))

const counterpartyLabel = ({ name, role }) => role ? `${name} (${role})` : name

function byCounterparty (rows) {
  const groups = new Map()
  for (const row of rows) {
    const group = groups.get(row.key) ?? { label: counterpartyLabel(row), activities: [] }
    group.activities.push(row.activity)
    groups.set(row.key, group)
  }
  return [...groups.values()]
}

function deductionNotes (rows) {
  const labelsByActivityId = new Map()
  for (const row of rows) {
    const id = row.activity.activityId
    labelsByActivityId.set(id, [...(labelsByActivityId.get(id) ?? []), counterpartyLabel(row)])
  }
  const labelsFor = activityId => [...new Set(labelsByActivityId.get(activityId) ?? [])]

  const notes = new Map()
  for (const { activity } of rows) {
    if (activity.deductFromActivityId) {
      const from = labelsFor(activity.deductFromActivityId)
      if (from.length) notes.set(activity.activityId, i18n.t('deductsFrom', { from: joinWithConjunction(from) }))
      continue
    }
    const deductors = [...new Set(rows
      .filter(row => row.activity.deductFromActivityId === activity.activityId)
      .map(counterpartyLabel))]
    if (deductors.length) notes.set(activity.activityId, i18n.t('deductedBy', { by: joinWithConjunction(deductors) }))
  }
  return notes
}

const GRID_COLUMNS = [
  { labelKey: 'activity', width: 150 },
  { labelKey: 'counterparty', width: 165 },
  { labelKey: 'product', width: 70 },
  { labelKey: 'payBy', width: 60, alignment: 'center' },
  { labelKey: 'payOn', width: 60, alignment: 'center' },
  { labelKey: 'rateSheet', width: '*' },
  { labelKey: 'rate', width: 'auto', alignment: 'right' }
]

const FLEXIBLE_WITHOUT_RATE_SHEETS = ['activity', 'counterparty']

function gridColumns (showRateSheets) {
  if (showRateSheets) return GRID_COLUMNS
  return GRID_COLUMNS
    .filter(column => column.labelKey !== 'rateSheet')
    .map(column => FLEXIBLE_WITHOUT_RATE_SHEETS.includes(column.labelKey) ? { ...column, width: '*' } : column)
}

function activityGrid (params, counterpartyRows, deductions, sheetNames) {
  const { contract } = params

  const showRateSheets = counterpartyRows.some(({ activity }) => rateSheetName(activity.baseCost, sheetNames) !== null)
  const columns = gridColumns(showRateSheets)
  const pad = row => row.map((cell, index) => ({ ...cell, alignment: columns[index].alignment }))

  const rows = []
  // An activity and its product boxes share one stripe, so the alternation runs per activity
  // rather than per row and a Products row never reads as belonging to the activity below it.
  const fills = []
  let group = 0

  for (const counterparty of counterpartyRows) {
    const { activity } = counterparty
    const suffix = distanceSuffix(activity, contract)
    const boxes = productBoxes(activity, sheetNames)
    const fill = group++ % 2 === 0 ? 'lightgrey' : 'white'

    fills.push(fill)
    rows.push(pad([
      { text: activity.activityTemplate.name, style: 'header' },
      counterpartyCell(counterparty, deductions.get(activity.activityId)),
      { text: i18n.t('base'), style: 'header' },
      { text: activity.baseCost ? PayBy.fromInt(activity.baseCost.payBy) : i18n.t('notApplicable'), style: 'header' },
      { text: activity.baseCost ? PayOn.fromInt(activity.baseCost.payOn) : i18n.t('notApplicable'), style: 'header' },
      ...(showRateSheets ? [{ text: rateSheetName(activity.baseCost, sheetNames) ?? i18n.t('notApplicable'), style: 'header' }] : []),
      { text: rateText(activity.baseCost, suffix), style: 'header' }
    ]))

    if (boxes.length) {
      fills.push(fill)
      rows.push([
        { ...chipsTable(boxes, CONTENT_WIDTH - GRID_CELL_PADDING), colSpan: columns.length },
        ...Array.from({ length: columns.length - 1 }, () => ({ text: '' }))
      ])
    }
  }

  const headerRow = columns.map(column => ({ text: i18n.t(column.labelKey), style: 'header', alignment: column.alignment }))

  return {
    layout: {
      ...gridRowFillLayout,
      fillColor: rowIndex => rowIndex === 0 ? 'white' : fills[rowIndex - 1]
    },
    table: {
      widths: columns.map(column => column.width),
      headerRows: 1,
      dontBreakRows: true,
      body: [headerRow, ...rows]
    }
  }
}

function counterpartyCell ({ name, role, isRuntime }, note) {
  const nameLine = { text: name, style: 'header' }
  const roleLine = role ? { text: role, style: 'subtitle' } : undefined
  return {
    stack: [
      isRuntime && !roleLine ? runtimeMarked(nameLine) : nameLine,
      ...(roleLine ? [runtimeMarked(roleLine)] : []),
      ...(note ? [{ text: note, style: 'subtitle', italics: true }] : [])
    ]
  }
}

const runtimeMarked = line => ({
  columns: [
    { svg: generateSvgForPdf(alphaRCircleOutline), width: 10 },
    { ...line, width: '*' }
  ],
  columnGap: 3
})

// The fetched sheets are the reliable source of a name; the cost's own copy is the fallback.
function rateSheetName (cost, sheetNames) {
  if (!cost?.rateSheetId) return null
  return sheetNames.get(cost.rateSheetId) ?? embeddedRateSheetName(cost) ?? null
}

// A rate sheet governs the price, and the Rate Sheet column already names it, so the numeric rate
// stored on the cost is not the figure to print.
const rateText = (cost, suffix = '') => !cost || cost.rateSheetId
  ? i18n.t('notApplicable')
  : formatMoney(cost.rate) + suffix

// Every product gets a box under its activity's base row. Anything that sets the product apart
// from that base row — its own pay terms, a rate sheet — rides inside the box, so no product is
// ever demoted to a plain row to make room for those fields.
function productBoxes (activity, sheetNames) {
  return sortByProductName(activity.activityDetails).map(detail => {
    const notes = []

    if (detail.cost && (!activity.baseCost ||
      detail.cost.payBy !== activity.baseCost.payBy ||
      detail.cost.payOn !== activity.baseCost.payOn)) {
      notes.push(`${PayBy.fromInt(detail.cost.payBy)} / ${PayOn.fromInt(detail.cost.payOn)}`)
    }

    const sheetName = rateSheetName(detail.cost, sheetNames)
    if (sheetName) notes.push(sheetName)

    return { name: detail.product.name, rate: rateText(detail.cost), notes }
  })
}

function chipWidth ({ name, rate, notes }) {
  const heading = (name.length * BOLD_CHAR) + CHIP_GAP + (rate.length * CHAR)
  return Math.max(heading, ...notes.map(note => note.length * CHAR)) + CHIP_CHROME
}

function chipRows (chips, available) {
  const rows = []
  let current = []
  let used = 0

  for (const box of chips) {
    const width = chipWidth(box)
    const extended = current.length ? used + CHIP_GAP + width : width
    if (current.length && extended > available) {
      rows.push(current)
      current = [box]
      used = width
    } else {
      current.push(box)
      used = extended
    }
  }
  if (current.length) rows.push(current)

  return rows
}

const chipsRowLayout = {
  hLineWidth: () => 0,
  vLineWidth: () => 0,
  paddingLeft: () => 0,
  paddingRight: () => CHIP_GAP,
  paddingTop: () => 0,
  paddingBottom: () => 4
}

// Boxes size to their content, as the UI's product cards do at xl. Each row is its own table so
// its columns pack against that row's boxes alone -- one shared table would widen every column to
// the widest box in the whole set -- with a trailing star column soaking up the slack.
function chipsTable (chips, available) {
  return {
    stack: chipRows(chips, available).map(row => ({
      table: {
        widths: [...row.map(() => 'auto'), '*'],
        body: [[...row.map(chip), { text: '' }]]
      },
      layout: chipsRowLayout
    }))
  }
}

function chip ({ name, rate, notes }) {
  return {
    table: {
      widths: ['*', 'auto'],
      body: [
        [
          { text: name, style: 'header' },
          { text: rate, style: 'subtitle', alignment: 'right' }
        ],
        ...notes.map(note => [{ text: note, style: 'subtitle', colSpan: 2 }, {}])
      ]
    },
    layout: {
      hLineWidth: (index, node) => index === 0 || index === node.table.body.length ? 0.5 : 0,
      vLineWidth: (index, node) => index === 0 || index === node.table.widths.length ? 0.5 : 0,
      hLineColor: () => CHIP_BORDER_COLOR,
      vLineColor: () => CHIP_BORDER_COLOR,
      paddingLeft: () => 6,
      paddingRight: () => 6,
      paddingTop: rowIndex => rowIndex === 0 ? 4 : 1,
      paddingBottom: (rowIndex, node) => rowIndex === node.table.body.length - 1 ? 4 : 0
    }
  }
}

// Fetched once up front so the Activities grid and the Rate Sheets section agree on every name
// and a sheet shared by two counterparties is still only fetched once per counterparty group.
async function fetchRateSheetsByCounterparty (rows) {
  const groups = []
  for (const { label, activities } of byCounterparty(rows)) {
    const references = getRateSheets(activities)
    if (!references.length) continue

    const sheets = []
    for (const reference of references) {
      sheets.push({ ...reference, rateSheet: await fetchRateSheetById(reference.rateSheetId) })
    }
    groups.push({ label, sheets })
  }
  return groups
}

const rateSheetNamesById = groups => new Map(groups.flatMap(({ sheets }) =>
  sheets.map(({ rateSheetId, rateSheet }) => [rateSheetId, rateSheet.name])))

function rateSheetSection (groups) {
  if (!groups.length) return []

  const blocks = groups.flatMap(({ label, sheets }) =>
    sheets.map(({ activityName, rateSheet }, index) => ({
      stack: [
        ...(index === 0 ? [{ text: label, style: 'sectionTitle', margin: getMargin({ top: 8 }) }] : []),
        { text: `${activityName}: ${rateSheet.name}`, style: 'header', margin: getMargin({ top: 8 }) },
        rateSheetTableBlock(rateSheet, { top: 6, bottom: 14 })
      ]
    })))

  // Only sheets after the first are movable. The first one stays under the Rate Sheets heading
  // however long it is — the heading counts as preceding content, so letting it move would strand
  // the heading alone on an otherwise empty page.
  return [
    { text: i18n.t('rateSheets'), style: 'title', pageBreak: 'before' },
    ...blocks.map((block, index) => index === 0 ? block : { ...block, headlineLevel: RATE_SHEET_BLOCK })
  ]
}
